import { Helmet } from '@dr.pogodin/react-helmet';
import {
  ChatCircleIcon,
  ChatIcon,
  FlagIcon,
  InfoIcon,
} from '@phosphor-icons/react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { css, html } from 'react-strict-dom';

import { Api } from '../../../api';
import {
  DateWithTooltip,
  PageError,
  PageHeader,
  PageSkeleton,
  PageWrap,
  PageWrapPadding,
  RouterLink,
  Stack,
  Tabs,
  Text,
} from '../../../components';
import { Color, Style } from '../../../constants';
import { color } from '../../../constants/color.css';
import { useClientData, useCompany } from '../../../contexts/ClientDataContext';
import { useAuthUser } from '../../../hooks';
import { Model } from '../../../models';
import { isAgency } from '../../../reducers/auth';
import { TicketActionsCards } from '../components';
import TicketBadge from '../components/TicketBadge';
import TicketCommentsThread from '../components/TicketCommentsThread';
import TicketTitle from '../components/TicketTitle';
import {
  findLabelGroupByName,
  getLabelGroups,
  getPriorityFlagColor,
  isInGroup,
} from '../util';

export default function TicketPage() {
  const { companySlug, ticketNumber } = useParams<{
    companySlug: string;
    ticketNumber: string;
  }>();
  const company = useCompany(companySlug!);
  const authUser = useAuthUser();
  const showInternal = isAgency(authUser);
  const { ticketLabels } = useClientData();
  const labelGroups = getLabelGroups(company, ticketLabels);
  const priorityGroup = findLabelGroupByName(labelGroups, 'Priority');
  // TODO: Compare companySlug with ticket.company?

  const apiTicket = Api.Ticket.useReadItemCompanySub(company.id, ticketNumber!);

  const { data: agencyUsersData } = Api.User.useReadCollectionAgency();
  const allUsers = agencyUsersData?.['hydra:member'] ?? [];
  const agencyUsers = allUsers.filter((u) => u.agencyRole !== null);

  const [mobileTab, setMobileTab] = React.useState<'comments' | 'information'>(
    'comments'
  );

  if (apiTicket.error) return <PageError {...apiTicket} />;
  if (!apiTicket.data) return <PageSkeleton variant="ticket-detail" />;

  const ticket = apiTicket.data;

  const priorityLabel = priorityGroup
    ? ticket.labels.find(isInGroup(priorityGroup))
    : undefined;

  return (
    <PageWrap background={Color.neutral5} noPadding>
      <Helmet>
        <title>{`${ticket.title} - Ticket #${ticket.number} | ${company.name}`}</title>
      </Helmet>

      <PageWrapPadding top horizontal bottom>
        <Stack gap={0.5}>
          <PageHeader
            title={
              <Stack
                style={[Style.row, Style.alignCenter, Style.wrap]}
                gap={0.5}
              >
                {ticket.closedAt ? (
                  <TicketBadge
                    label="Closed"
                    bgColor={Color.blueGreyBg}
                    textColor={Color.blueGrey}
                    regular
                  />
                ) : (
                  <TicketBadge
                    label="In Progress"
                    textColor="#A06103"
                    bgColor={Color.yellowBg}
                    bordered
                    regular
                  />
                )}
                {priorityLabel && (
                  <Stack style={[Style.row, Style.alignCenter]} gap={0.25}>
                    <FlagIcon
                      size={16}
                      weight="fill"
                      color={getPriorityFlagColor(priorityLabel.name)}
                    />
                    <Text
                      typeface="body.small.regular"
                      style={styles.fontNormal}
                      color="accent"
                    >
                      {priorityLabel.name}
                    </Text>
                  </Stack>
                )}
                <Text
                  typeface="headline"
                  style={styles.fontNormal}
                  color="neutral60"
                >
                  #{ticket.number}
                </Text>
                <TicketTitle
                  ticket={ticket}
                  invalidate={apiTicket.invalidate}
                />
              </Stack>
            }
            back={{
              label: 'Maintenance Tickets',
              to: `/company/${companySlug}/tickets`,
            }}
          />
          <Stack style={[Style.row, Style.alignCenter]} gap={0.5}>
            <Text typeface="body.small.regular" color="neutral60">
              Opened <DateWithTooltip timestamp={ticket.createdAt} />
            </Text>
            <html.div style={styles.divider} />
            <Stack style={[Style.row, Style.alignCenter]} gap={0.25}>
              <ChatIcon size={16} color={color.neutral60} />
              <Text typeface="body.small.regular" color="neutral60">
                {ticket.comments ? ticket.comments.length : 0}
              </Text>
            </Stack>
            {ticket.release && (
              <>
                {' · '}
                <RouterLink
                  to={`/company/${company.slug}/releases/${ticket.release.versionNumber}`}
                >
                  {Model.Release.getVersionName(ticket.release)}
                </RouterLink>
              </>
            )}
          </Stack>
        </Stack>

        <html.div style={styles.mobileTabBar}>
          <Tabs
            accentColor={Color.accent}
            fullWidth
            centered
            tabs={[
              {
                key: 'comments',
                label: 'Comments',
                icon: ({ size, color: iconColor }) => (
                  <ChatCircleIcon size={size} color={iconColor} />
                ),
                selected: mobileTab === 'comments',
                onClick: () => setMobileTab('comments'),
              },
              {
                key: 'information',
                label: 'Information',
                icon: ({ size, color: iconColor }) => (
                  <InfoIcon size={size} color={iconColor} />
                ),
                selected: mobileTab === 'information',
                onClick: () => setMobileTab('information'),
              },
            ]}
          />
        </html.div>

        <Stack style={[Style.row, styles.columns]} gap={1.5}>
          <Stack
            gap={1.5}
            style={[
              Style.fill,
              styles.timelineColumn,
              mobileTab !== 'comments' && styles.hiddenOnMobile,
            ]}
          >
            <TicketCommentsThread
              ticket={ticket}
              showInternal={showInternal}
              invalidate={apiTicket.invalidate}
              companySlug={companySlug!}
              allUsers={allUsers}
            />
          </Stack>

          <TicketActionsCards
            ticket={ticket}
            company={company}
            invalidate={apiTicket.invalidate}
            agencyUsers={agencyUsers}
            labelGroups={labelGroups}
            style={mobileTab !== 'information' && styles.hiddenOnMobile}
          />
        </Stack>
      </PageWrapPadding>
    </PageWrap>
  );
}

const styles = css.create({
  fontNormal: {
    fontWeight: '400',
  },
  mobileTabBar: {
    display: {
      default: 'block',
      '@media (min-width: 1200px)': 'none',
    },
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: color.neutral20,
    marginTop: 16,
    marginBottom: 16,
  },
  columns: {
    flexDirection: {
      default: 'row',
      '@media (max-width: 1199px)': 'column',
    },
  },
  timelineColumn: {
    minWidth: 0,
  },
  hiddenOnMobile: {
    display: {
      default: 'flex',
      '@media (max-width: 1199px)': 'none',
    },
  },
  status: {
    borderRadius: 3,
    color: '#fff',
    display: 'inline-block',
    fontWeight: '500',
    lineHeight: '20px',
    paddingBlock: 4,
    paddingInline: 8,
    textAlign: 'center',
    marginRight: 8,
  },
  statusOpen: {
    backgroundColor: '#2cbe4e',
  },
  statusClosed: {
    backgroundColor: '#1f78d1',
  },
  detailsLine: {
    marginBottom: 25,
    color: color.neutral60,
    fontSize: 14,
  },
  bold: {
    fontWeight: '700',
    color: color.neutral100,
    marginRight: 5,
  },
  avatarWrap: {
    display: 'inline-block',
    verticalAlign: 'middle',
    marginInline: 5,
  },
  desktopOnly: {
    display: {
      default: 'block',
      '@media (max-width: 470px)': 'none',
    },
  },
  mobileOnly: {
    display: {
      default: 'none',
      '@media (max-width: 470px)': 'block',
    },
  },
  timelineSpine: {
    position: 'absolute',
    top: 20,
    left: 40,
    bottom: 20,
    width: 1,
    backgroundColor: '#ddd',
  },
  divider: {
    height: 20,
    width: 1,
    backgroundColor: color.neutral20,
  },
});
